import { computed, onMounted, type Component } from 'vue';
import { createApp } from 'vue';
import { sleep } from '@/js/lib/helper/sleep';
import { uniqueId } from '@/js/lib/helper/unique-id';
import IModal from '@/js/components/molecules/IModal.vue';
type BaseArgs = {
  Component?: Component;
  props?: Record<string, unknown>;
  slot?: string | string[];
  canScroll?: boolean;
};

export function mountModal({ Component, props, slot, canScroll }: BaseArgs): Promise<boolean>;
export function mountModal<T>({ Component, props, slot, asyncFunc, canScroll }: BaseArgs & { asyncFunc: () => Promise<T> }): Promise<T>;
export function mountModal<T>({ Component, props, slot, asyncFunc, canScroll }: BaseArgs & { asyncFunc?: () => Promise<T> } = {}): Promise<T | boolean> {
  const mountDiv = document.createElement('div');
  document.body.append(mountDiv);
  return new Promise((resolve, reject) => {
    const onClose = async () => {
      resolve(false);
      await sleep(300);
      mountDiv.remove();
    };
    const onCancel = async () => {
      resolve(false);
      await sleep(300);
      mountDiv.remove();
    };
    const onOk = () => {
      resolve(true);
    };

    const app = createApp({
      components: { Modal: Component || IModal },
      setup() {
        const texts = computed(() => (Array.isArray(slot) ? slot : [slot]));

        onMounted(async () => {
          // 非同期処理のローディング用
          if (asyncFunc !== undefined) {
            try {
              const res = await asyncFunc();
              resolve(res);
            } catch (error) {
              reject(error);
            } finally {
              await sleep(300);
              app.unmount();
              mountDiv.remove();
            }
          }
        });

        return {
          props,
          canScroll,
          texts,
          uniqueId,
          onClose,
          onCancel,
          onOk,
        };
      },
      template: `<modal @close="onClose" @cancel="onCancel" @ok="onOk" v-bind="props" :append-to-body="false" :lock-scroll="!canScroll">
        <p v-for="text in texts" :key="uniqueId()" style="margin: 6px 0; white-space: pre-wrap;">{{ text }}</p>
      </modal>`,
    });
    app.mount(mountDiv);
  });
}
